import { createContext, useContext, useEffect, useState } from "react";

// Contexts
import { TagsContext } from "./tags";

// Platforms
import { Api } from "../platforms/api";

// Utils
import { reformatAcfResponse } from "../utils/fetchUtils";
import { scrollToTop } from "../utils/scrollUtils";
import { generateFilteredVideos } from "../utils/videoUtils";

export const VideosContext = createContext({});

export const VideosProvider = (props) => {
    const { children } = props;

    /************************************************
     * useContext
     ************************************************/

    const {
        hasSelectedCategoryTags,
        hasSelectedConferenceTags,
        hasSelectedTags,
        selectedCategoryTags,
        selectedConferenceTags,
        selectedTags,
    } = useContext(TagsContext);

    /************************************************
     * useState
     ************************************************/

    const [currentPage, setCurrentPage] = useState(1);
    const [filteredVideos, setFilteredVideos] = useState([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [totalPages, setTotalPages] = useState(null);
    const [totalVideos, setTotalVideos] = useState(null);
    const [videos, setVideos] = useState([]);
    const [videoSearchResults, setVideoSearchResults] = useState([]);

    /************************************************
     * useEffect
     ************************************************/

    useEffect(() => {
        fetchVideos();
        scrollToTop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, selectedTags]);

    useEffect(() => {
        // handleFiltering();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTags, selectedCategoryTags, selectedConferenceTags]);

    /************************************************
     * Constants
     ************************************************/

    const shouldShowFilteredVideos =
        hasSelectedTags && hasSelectedCategoryTags && hasSelectedConferenceTags;

    /************************************************
     * End Hooks
     ************************************************/

    function fetchVideos() {
        setCurrentPage(1);
        setIsSearching(true);
        setIsSearchActive(true);
        return Api.getVideos(
            { currentPage, operator: "OR", selectedTags },
            onFetchVideosSuccess,
            onFetchVideosError,
            onFetchVideosError
        );
    }

    function fetchVideoSearchResults(search) {
        setCurrentPage(1);
        setIsSearching(true);
        handleToggleSearchMode(true);
        return Api.getVideosBySearchTerm(
            { search },
            onFetchVideoSearchResultsSuccess,
            onFetchVideoSearchResultsError
        );
    }

    function handleFiltering(res) {
        if (
            hasSelectedTags &&
            hasSelectedCategoryTags &&
            hasSelectedConferenceTags
        ) {
            setIsFiltering(true);
            setFilteredVideos(
                generateFilteredVideos(
                    res,
                    selectedConferenceTags,
                    selectedCategoryTags
                )
            );
            setIsFiltering(false);
        }
    }

    function handleSelectPage({ selected }) {
        const selectedPage = selected + 1;
        setCurrentPage(selectedPage);
    }

    function handleToggleSearchMode(bool) {
        setIsSearchActive(bool);
    }

    function onFetchVideosError() {
        setIsSearching(false);
        setIsSearchActive(false);
    }

    function onFetchVideosSuccess({ headers, body: res }) {
        const reformattedResponse = reformatAcfResponse(res);
        setIsSearching(false);
        setIsSearchActive(false);
        setTotalVideos(parseInt(headers.get("X-Wp-Total")));
        setTotalPages(parseInt(headers.get("X-Wp-Totalpages")));
        setVideos(reformattedResponse);
        handleFiltering(reformattedResponse);
    }

    function onFetchVideoSearchResultsError(err) {
        setIsSearching(false);
    }

    function onFetchVideoSearchResultsSuccess({ headers, body: res }) {
        setVideoSearchResults(reformatAcfResponse(res));
        setIsSearching(false);
    }

    const videosContext = {
        fetchVideos,
        fetchVideoSearchResults,
        filteredVideos,
        handleSelectPage,
        handleToggleSearchMode,
        isFiltering,
        isSearchActive,
        isSearching,
        shouldShowFilteredVideos,
        totalPages,
        totalVideos,
        videos,
        videoSearchResults,
    };

    return (
        <VideosContext.Provider value={videosContext}>
            {children}
        </VideosContext.Provider>
    );
};
